import React, { useState } from 'react'
import { addMonths } from 'date-fns/addMonths'
import {
  Button,
  cn,
  Label,
  PlainA,
  RadioGroup,
  CheckboxWithLabel,
  Span,
  DatePicker,
  Select,
  Divider,
  TravelerSelection
} from '@pcln/horizon'
import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import { generateTimeSelectOptions } from '@/components/TimeSelect/TimeSelect'
import useBootstrapData from '@/hooks/useBootstrapData'
import { DEFAULT_VALUES, LABELS } from '@/components/Cars/HorizonForm/constants'
import { useForm } from 'react-hook-form'
import type { TravelerState } from '@/types'
import { yupResolver } from '@hookform/resolvers/yup'
import useTabGroup from '@/hooks/useTabGroup'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import type { PackageFormTripType, PackagesFormStateType } from '../types'
import {
  getGridContainerClass,
  horizonPackagesForm
} from './HorizonPackagesForm.variants'
import {
  STAY_FLY,
  STAY_FLY_DRIVE,
  CHECKBOX_TEXT,
  RADIO_OPTIONS
} from './constants'
import { STAY_DRIVE } from '../SearchForm/TripTypes'
import usePackagesFormSubmitValues from './usePackagesFormSubmitValues'
import { initialState } from '../SearchForm/utils'
import {
  isInfantLimitReachedPackages,
  isInfantOnInternationalTravel,
  searchFormSchema
} from '../SearchForm/validation'
import {
  handleDateRangeCalendarChange,
  handleSingleDateCalendarChange,
  isString
} from './utils'

export default function HorizonPackagesForm({
  onSubmit,
  pageName
}: {
  onSubmit: (
    pageName: 'homepage' | 'landing',
    data: PackagesFormStateType
  ) => void
  pageName: 'homepage' | 'landing'
}) {
  const {
    isMobile,
    webstats: { clientGUID: cguid },
    locale: { localeCode = 'en-US' } = {}
  } = useBootstrapData()
  const {
    radioButtons,
    originInput,
    destinationInput,
    timelineInput,
    passengersInput,
    advancedSearchSlot,
    checkBoxSlot,
    partStaySlot,
    carPickupSlot
  } = horizonPackagesForm()

  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
    setValue
  } = useForm<PackagesFormStateType>({
    defaultValues: initialState,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange'
  })

  const {
    watchStartLocation,
    watchEndLocation,
    watchTravelers,
    watchTripType,
    watchRcLocation
  } = usePackagesFormSubmitValues({
    control,
    clearErrors,
    initialValues: initialState
  })

  const [packagesType, setPackagesType] = useState<PackageFormTripType>(
    initialState.tripType
  )
  const [isHotelPartialStay, setIsHotelPartialStay] = useState(false)
  const [isCarPickupElsewhere, setIsCarPickupElsewhere] = useState(false)
  const [pickupTime, setPickupTime] = useState<string>(
    DEFAULT_VALUES.PICKUP_TIME
  )
  const [dropoffTime, setDropoffTime] = useState<string>(
    DEFAULT_VALUES.DROPOFF_TIME
  )
  const { registerTabElement } = useTabGroup()
  const hasOnlyHotelAndCar = packagesType === STAY_DRIVE
  const hasHotelIncluded = packagesType.includes('STAY')
  const hasHotelAndFlightIncluded =
    packagesType === STAY_FLY || packagesType === STAY_FLY_DRIVE
  const hasHotelPartStayIncluded =
    isHotelPartialStay && hasHotelAndFlightIncluded

  const handleSubmitHelper = handleSubmit((data: PackagesFormStateType) => {
    onSubmit(pageName, data)
    logSearchFormAnalytics(cguid, pageName, 'vacations', isMobile)
  })
  const currentDate = new Date()
  const calendarMaxMonth = addMonths(currentDate, 11)

  return (
    <div className="w-auto">
      <form
        className="w-auto"
        aria-label="horizon-packages-search-form"
        onSubmit={handleSubmitHelper}
      >
        <div className="w-auto mx-auto">
          <div
            className={getGridContainerClass(
              packagesType,
              isHotelPartialStay,
              isCarPickupElsewhere
            )}
          >
            {/* Packages Type Radio Buttons */}
            <div className={radioButtons()}>
              <RadioGroup
                className="grid grid-rows-2 sm:grid-rows-1 grid-flow-col auto-cols-min gap-x-10 sm:gap-4"
                name="packagesType"
                defaultValue={STAY_FLY}
                options={RADIO_OPTIONS}
                handleChange={(value: unknown) => {
                  if (isString(value)) {
                    setPackagesType(value)
                    setValue('tripType', value)
                  }
                }}
              />
            </div>
            {/* Start Location */}
            {!hasOnlyHotelAndCar && (
              <div className={originInput()}>
                <AutocompleteWrapper
                  searchKey="startLocation"
                  label="Where from?"
                  typeAheadEndpoint="packagesOrigin"
                  onItemSelect={item => {
                    setValue('startLocation', item)
                  }}
                  errorMessage={errors.startLocation?.message}
                  ref={(elem: HTMLInputElement) => {
                    registerTabElement(elem)
                  }}
                  initialSelectedItem={watchStartLocation ?? undefined}
                />
              </div>
            )}
            {/* End Location */}
            <div className={destinationInput()}>
              <AutocompleteWrapper
                searchKey="endLocation"
                label="Where to?"
                typeAheadEndpoint="hotels"
                onItemSelect={item => {
                  setValue('endLocation', item)
                }}
                errorMessage={errors.endLocation?.message}
                ref={(elem: HTMLInputElement) => {
                  registerTabElement(elem)
                }}
                initialSelectedItem={watchEndLocation ?? undefined}
              />
            </div>

            {/* Date Picker */}
            <div
              data-testid="date-picker-departing-returning"
              className={timelineInput()}
            >
              <DatePicker
                isMobile={isMobile}
                label="Departing - Returning"
                mode="range"
                numberOfMonths={isMobile ? 12 : 2}
                handleCalendarChange={dates => {
                  handleDateRangeCalendarChange(
                    setValue,
                    'startDate',
                    'endDate',
                    dates
                  )
                }}
                errorMessage={
                  errors.startDate?.message || errors.endDate?.message
                }
                inputRef={registerTabElement}
                disabled={{
                  before: currentDate
                }}
                startMonth={currentDate}
                endMonth={calendarMaxMonth}
              />
            </div>
            {/* Hotel Part Stay Checked - Date Picker (check-in/out)  */}
            {hasHotelPartStayIncluded && (
              <div
                data-testid="date-picker-hotel-part-stay"
                className={partStaySlot()}
              >
                <DatePicker
                  isMobile={isMobile}
                  label="Check-in - Check-out"
                  mode="range"
                  numberOfMonths={isMobile ? 12 : 2}
                  handleCalendarChange={dates => {
                    handleDateRangeCalendarChange(
                      setValue,
                      'hotelStartDate',
                      'hotelEndDate',
                      dates
                    )
                  }}
                  errorMessage={
                    errors.hotelStartDate?.message ||
                    errors.hotelEndDate?.message
                  }
                  inputRef={registerTabElement}
                  disabled={{
                    before: currentDate
                  }}
                  startMonth={currentDate}
                  endMonth={calendarMaxMonth}
                />
              </div>
            )}
            {/* Travelers */}
            <div className={passengersInput()}>
              <TravelerSelection
                isMobile={isMobile}
                isHotel={hasHotelIncluded}
                initialValues={initialState.travelers}
                roomsEnabled={hasHotelIncluded}
                ageSelectEnabled
                maxTravelers={8}
                maxChildren={6}
                tooltipErrorMessageId="travelerSelectionError"
                onChange={values => {
                  setValue('travelers', values as TravelerState)
                }}
                errorMessage={
                  isInfantOnInternationalTravel(
                    watchStartLocation,
                    watchEndLocation,
                    watchTravelers.childrenAges,
                    watchTripType
                  ) ||
                  isInfantLimitReachedPackages(
                    watchTravelers.adults,
                    watchTravelers.childrenAges,
                    watchTripType
                  ) ||
                  errors?.travelers?.childrenAges?.message
                }
              />
            </div>
            {/* Pickup Car Elsewhere */}
            {isCarPickupElsewhere && hasOnlyHotelAndCar && (
              <div className={carPickupSlot()}>
                <div className="[grid-area:city]">
                  <AutocompleteWrapper
                    searchKey="cars"
                    label="City, Airport or Address"
                    typeAheadEndpoint="cars"
                    onItemSelect={item => {
                      setValue('rcLocation', item)
                    }}
                    errorMessage={errors.rcLocation?.message}
                    ref={(elem: HTMLInputElement) => {
                      registerTabElement(elem)
                    }}
                    initialSelectedItem={watchRcLocation ?? undefined}
                  />
                </div>
                <div className="**:translate-y-0 [grid-area:pickupDate]">
                  <DatePicker
                    isMobile={isMobile}
                    label="Pickup"
                    mode="single"
                    numberOfMonths={isMobile ? 12 : 1}
                    handleCalendarChange={date => {
                      handleSingleDateCalendarChange(
                        setValue,
                        'rcPickupDate',
                        date
                      )
                    }}
                    errorMessage={errors.rcPickupDate?.message}
                    inputRef={registerTabElement}
                    disabled={{
                      before: currentDate
                    }}
                    startMonth={currentDate}
                    endMonth={calendarMaxMonth}
                  />
                </div>
                <div className="[grid-area:pickupTime]">
                  <Select
                    label={LABELS.PICKUP_TIME}
                    isInset
                    menuItems={generateTimeSelectOptions(localeCode, true)}
                    initialValue={pickupTime}
                    onChange={item => {
                      setPickupTime(item)
                      setValue('rcPickupTime', item)
                    }}
                    placeholder={LABELS.PICKUP_TIME}
                  />
                </div>
                <div className="**:translate-y-0 [grid-area:dropoffDate]">
                  <DatePicker
                    isMobile={isMobile}
                    label="Drop off"
                    mode="single"
                    numberOfMonths={isMobile ? 12 : 1}
                    handleCalendarChange={date => {
                      handleSingleDateCalendarChange(
                        setValue,
                        'rcDropoffDate',
                        date
                      )
                    }}
                    errorMessage={errors.rcDropoffDate?.message}
                    inputRef={registerTabElement}
                    disabled={{
                      before: currentDate
                    }}
                    startMonth={currentDate}
                    endMonth={calendarMaxMonth}
                  />
                </div>
                <div className="[grid-area:dropoffTime]">
                  <Select
                    label={LABELS.DROPOFF_TIME}
                    isInset
                    menuItems={generateTimeSelectOptions(localeCode, true)}
                    initialValue={dropoffTime}
                    onChange={item => {
                      setDropoffTime(item)
                      setValue('rcDropoffTime', item)
                    }}
                    placeholder={LABELS.DROPOFF_TIME}
                  />
                </div>
              </div>
            )}
            {/* Search Button */}
            <Button
              buttonType="submit"
              type="primary"
              size="lg"
              className={cn('[grid-area:unset] sm:[grid-area:submit]')}
            >
              Search
            </Button>
            <div className="contents [grid-area:unset] lg:[grid-area:group] lg:flex items-center gap-2">
              {/* CheckBox */}
              {hasHotelIncluded && (
                <div className={checkBoxSlot()}>
                  <CheckboxWithLabel
                    checked={
                      packagesType === STAY_DRIVE
                        ? isCarPickupElsewhere
                        : isHotelPartialStay
                    }
                    handleChange={() => {
                      if (
                        packagesType === STAY_FLY ||
                        packagesType === STAY_FLY_DRIVE
                      ) {
                        setValue('partialStay', !isHotelPartialStay)
                        setIsHotelPartialStay(!isHotelPartialStay)
                      }
                      if (packagesType === STAY_DRIVE) {
                        setValue('pickupCarSomewhere', !isCarPickupElsewhere)
                        setIsCarPickupElsewhere(!isCarPickupElsewhere)
                      }
                    }}
                    labelNode={
                      <Span textStyle="body3">
                        {CHECKBOX_TEXT[packagesType]}
                      </Span>
                    }
                    size="16"
                    name="checkbox-hotel-part-stay"
                    value="hotel"
                  />
                </div>
              )}
              {/* Divider */}
              {hasHotelAndFlightIncluded && (
                <div className="hidden h-4 lg:flex flex-row justify-center">
                  <Divider direction="vertical" />
                </div>
              )}
              {/* Advanced Search */}
              {!hasOnlyHotelAndCar && (
                <div className={advancedSearchSlot()}>
                  <Label textStyle="body3">
                    One-way flights or multiple hotels:
                  </Label>
                  <PlainA
                    textStyle="body3"
                    className="font-bold"
                    iconRight="arrow_forward"
                    href="/shop/search/trips/?adults=2&ages=%7B%7D&children=0&end=&flight-code=RT&rooms=1&start=&travelers=%5Bobject%20Object%5D&trip-type=STAY%3AFLY"
                    target="_blank"
                  >
                    Advanced Search
                  </PlainA>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
